import { AuthBaseClient } from '../base-client';
import type {
  PayorResponse,
  PayorPlanResponse,
  PayorPlanWithPayorName,
  AcceptedPayorWithNames,
  AcceptedPayorResponse,
  AddAcceptedPayorRequest,
  UpdateAcceptedPayorRequest,
  PayorMatchResultResponse,
  PayorImportResult,
} from './types';

/**
 * Client for payor API endpoints.
 */
export class PayorClient extends AuthBaseClient {
  // ==========================================================================
  // Payors (reference data)
  // ==========================================================================

  async list(): Promise<PayorResponse[]> {
    return this.get<PayorResponse[]>('/api/payors');
  }

  async search(query: string, limit?: number): Promise<PayorResponse[]> {
    const params = new URLSearchParams({ q: query });
    if (limit) params.set('limit', String(limit));
    return this.get<PayorResponse[]>(`/api/payors/search?${params}`);
  }

  async getById(id: string): Promise<PayorResponse> {
    return this.get<PayorResponse>(`/api/payors/${id}`);
  }

  async getPlans(payorId: string): Promise<PayorPlanResponse[]> {
    return this.get<PayorPlanResponse[]>(`/api/payors/${payorId}/plans`);
  }

  // ==========================================================================
  // Plan search
  // ==========================================================================

  async searchPlans(query: string, limit?: number): Promise<PayorPlanWithPayorName[]> {
    const params = new URLSearchParams({ q: query });
    if (limit) params.set('limit', String(limit));
    return this.get<PayorPlanWithPayorName[]>(`/api/payors/plans/search?${params}`);
  }

  // ==========================================================================
  // Import / Export
  // ==========================================================================

  /**
   * Import payor catalog from CSV file.
   * Uses multipart/form-data (can't use the base post which JSON.stringifies).
   */
  async importCsv(file: File): Promise<PayorImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${this.baseUrl}/api/payors/catalog/import`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    const body = (await response.json()) as {
      data?: PayorImportResult;
      error?: { message?: string };
    };
    if (!response.ok) {
      throw new Error(body.error?.message ?? 'Import failed');
    }
    return body.data as PayorImportResult;
  }

  /** Get the export URL (for browser download via window.open or anchor click). */
  getExportUrl(): string {
    return `${this.baseUrl}/api/payors/catalog/export`;
  }

  // ==========================================================================
  // Payor matching
  // ==========================================================================

  async matchPayor(insuranceText: string): Promise<PayorMatchResultResponse> {
    return this.post<PayorMatchResultResponse>('/api/payors/match', { insuranceText });
  }

  // ==========================================================================
  // Accepted payors (org-scoped)
  // ==========================================================================

  async listAccepted(includeInactive = false): Promise<AcceptedPayorWithNames[]> {
    const params = includeInactive ? '?includeInactive=true' : '';
    return this.get<AcceptedPayorWithNames[]>(`/api/payors/accepted/list${params}`);
  }

  async reactivatePayor(payorId: string): Promise<{ reactivated: number }> {
    return this.post<{ reactivated: number }>(
      `/api/payors/accepted/reactivate-by-payor/${payorId}`
    );
  }

  async addAccepted(data: AddAcceptedPayorRequest): Promise<AcceptedPayorResponse> {
    return this.post<AcceptedPayorResponse>('/api/payors/accepted', data);
  }

  async updateAccepted(
    id: string,
    data: UpdateAcceptedPayorRequest
  ): Promise<AcceptedPayorResponse> {
    return this.patch<AcceptedPayorResponse>(`/api/payors/accepted/${id}`, data);
  }

  async removeAccepted(id: string): Promise<void> {
    return this.delete<void>(`/api/payors/accepted/${id}`);
  }
}
